<template>
  <UserLayout pageTitle="Richieste di permesso">
    <template #page-content>
      <v-card :loading="loading" tile>
        <v-card-title>Richieste ricevute</v-card-title>

        <v-card-text v-if="richieste.length === 0">
          Nessuna richiesta di permesso.
        </v-card-text>

        <v-card-text v-else>
          <div
            v-for="op in richieste"
            :key="op.operatoreId"
            class="mb-6"
          >
            <!-- OPERATORE -->
            <h2 class="operatore">{{ op.operatoreNome }}</h2>
            <v-divider class="mb-3" />

            <!-- RICHIESTE -->
            <v-card
              v-for="req in op.richieste"
              :key="req.key"
              outlined
              class="mb-3"
            >
              <v-card-title>
                Richiesta {{ formatDateTime(req.dataCreazione) }}
              </v-card-title>

              <v-card-text>
                Dal <strong>{{ formatDate(req.dal) }}</strong>
                al <strong>{{ formatDate(req.al) }}</strong>
              </v-card-text>

              <!-- AZIONI DI GRUPPO -->
              <v-card-actions>
                <v-btn
                  small
                  color="green"
                  class="white--text"
                  @click="approvaRichiesta(req)"
                >
                  Approva tutto
                </v-btn>

                <v-btn
                  small
                  color="red"
                  class="white--text"
                  @click="declinaRichiesta(req)"
                >
                  Declina tutto
                </v-btn>

                <v-spacer />

                <v-btn text @click="req.open = !req.open">
                  {{ req.open ? "Nascondi dettagli" : "Mostra dettagli" }}
                </v-btn>
              </v-card-actions>

              <!-- EVENTI -->
              <v-expand-transition>
                <div v-show="req.open">
                  <v-divider />

                  <div
                    v-for="ev in req.eventi"
                    :key="ev.idIntervento"
                    class="evento"
                  >
                    <div>
                      {{ formatDate(ev.inizio) }}
                    </div>

                    <div>
                      <v-btn
                        small
                        color="green"
                        class="white--text mr-1"
                        @click="approvaEvento(ev)"
                      >
                        Approva
                      </v-btn>

                      <v-btn
                        small
                        color="red"
                        class="white--text"
                        @click="declinaEvento(ev)"
                      >
                        Declina
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-expand-transition>
            </v-card>
          </div>
        </v-card-text>
      </v-card>
    </template>
  </UserLayout>
</template>

<script>
import UserLayout from "@/layout/UserLayout.vue";
import moment from "moment";
import EasyPlanClient from "@/utils/EasyPlanClient";
import SessionHelper from "@/utils/SessionHelper";

export default {
  name: "PermissionsRequests",
  components: { UserLayout },

  data() {
    return {
      loading: false,
      richieste: []
    };
  },

  created() {
    this.loadEvents();
  },

  methods: {
    getAlias() {
      return this.$route.params.alias || this.$route.params.companyAlias;
    },

    formatDate(date) {
      return moment(date).format("DD MMM YYYY");
    },

    formatDateTime(date) {
      return moment(date).format("DD MMM YYYY HH:mm");
    },

    async loadEvents() {
      this.loading = true;
      const alias = this.getAlias();

      const fromDate = new Date();
      const toDate = new Date();
      toDate.setDate(toDate.getDate() + 30);

      try {
        const res = await EasyPlanClient.Event.FindByCompanyAliasDateFromTo(
          alias,
          fromDate,
          toDate,
          false
        );

        // 🔹 SOLO RICHIESTE ATTIVE
        const eventi = res.data.data.filter(
          ev => ev.tipo === "RichiestaPermesso" && !ev.rimosso
        );

        // 🔹 1) RAGGRUPPA PER OPERATORE
        const perOperatoreEvents = {};
        eventi.forEach(ev => {
          const operatoreId = ev.idCreatore;
          const operatoreNome =
            ev.creatore?.username ||
            `${ev.creatore?.name || ""} ${ev.creatore?.surname || ""}`.trim() ||
            "Sconosciuto";

          if (!perOperatoreEvents[operatoreId]) {
            perOperatoreEvents[operatoreId] = {
              operatoreId,
              operatoreNome,
              eventi: []
            };
          }

          perOperatoreEvents[operatoreId].eventi.push(ev);
        });

        // 🔹 2) PER OGNI OPERATORE: ORDINA PER GIORNO + CREA GRUPPI CONTIGUI
        const perOperatore = {};

        Object.values(perOperatoreEvents).forEach(opData => {
          // ordina per inizio
          opData.eventi.sort((a, b) => new Date(a.inizio) - new Date(b.inizio));

          // dedup: se per caso arrivano due eventi stesso giorno (difesa)
          const byDay = {};
          opData.eventi.forEach(ev => {
            const dayKey = moment(ev.inizio).format("YYYY-MM-DD");
            if (!byDay[dayKey]) byDay[dayKey] = ev;
          });
          const eventiUnici = Object.values(byDay).sort(
            (a, b) => new Date(a.inizio) - new Date(b.inizio)
          );

          const richiesteContigue = [];
          let currentGroup = null;

          for (const ev of eventiUnici) {
            const day = moment(ev.inizio).startOf("day");

            if (!currentGroup) {
              currentGroup = {
                eventi: [ev],
                dal: day.clone(),
                al: day.clone()
              };
              continue;
            }

            const nextExpected = currentGroup.al.clone().add(1, "day");

            if (day.isSame(nextExpected)) {
              currentGroup.eventi.push(ev);
              currentGroup.al = day.clone();
            } else {
              richiesteContigue.push(currentGroup);
              currentGroup = {
                eventi: [ev],
                dal: day.clone(),
                al: day.clone()
              };
            }
          }

          if (currentGroup) richiesteContigue.push(currentGroup);

          // 🔹 costruzione struttura finale (uguale a prima)
          perOperatore[opData.operatoreId] = {
            operatoreId: opData.operatoreId,
            operatoreNome: opData.operatoreNome,
            richieste: richiesteContigue.map((g, index) => {
              // ordina eventi nel gruppo
              g.eventi.sort((a, b) => new Date(a.inizio) - new Date(b.inizio));

              // dataCreazione: la UI la mostra in alto: metto quella del primo evento del gruppo
              const dataCreazione = g.eventi[0]?.dataCreazione || g.eventi[0]?.inizio;

              return {
                key: `${opData.operatoreId}_${g.dal.format("YYYYMMDD")}_${index}`,
                operatoreId: opData.operatoreId,
                operatoreNome: opData.operatoreNome,
                dataCreazione,
                eventi: g.eventi,
                open: false,
                dal: g.dal.format("YYYY-MM-DD"),
                al: g.al.format("YYYY-MM-DD")
              };
            })
          };
        });

        // 🔹 opzionale: ordina operatori per nome e richieste per dal
        const listaFinale = Object.values(perOperatore)
          .sort((a, b) => (a.operatoreNome || "").localeCompare(b.operatoreNome || ""))
          .map(op => {
            op.richieste.sort((a, b) => new Date(a.dal) - new Date(b.dal));
            return op;
          });

        this.richieste = listaFinale;
      } catch (err) {
        console.error("Errore caricamento richieste:", err);
      } finally {
        this.loading = false;
      }
    },

    // ✅ APPROVA SINGOLO EVENTO
    async approvaEvento(ev) {
      const alias = this.getAlias();

      const res = await EasyPlanClient.Event.Find(alias, ev.idIntervento);
      const evento = res.data.data;

      evento.tipo = "Assenza";
      evento.descrizione = "Ferie";
      evento.giornoIntero = true;
      evento.convertitoDaRichiestaPermesso = true;

      const currentUser = SessionHelper.getCurrentUser();
      evento.idCreatore = currentUser?.idUtente;

      await EasyPlanClient.Event.Update(alias, ev.idIntervento, evento);
      await this.loadEvents();
    },

    // ❌ DECLINA SINGOLO EVENTO
    async declinaEvento(ev) {
      const alias = this.getAlias();

      const res = await EasyPlanClient.Event.Find(alias, ev.idIntervento);
      const evento = res.data.data;

      evento.descrizione = "Richiesta ferie declinata";
      evento.rimosso = true;

      await EasyPlanClient.Event.Update(alias, ev.idIntervento, evento);
      await this.loadEvents();
    },

    // ✅ APPROVA TUTTA LA RICHIESTA
    async approvaRichiesta(req) {
      if (!confirm("Approvo tutta la richiesta?")) return;

      for (const ev of req.eventi) {
        await this.approvaEvento(ev);
      }
    },

    // ❌ DECLINA TUTTA LA RICHIESTA
    async declinaRichiesta(req) {
      if (!confirm("Declino tutta la richiesta?")) return;

      for (const ev of req.eventi) {
        await this.declinaEvento(ev);
      }
    }
  }
};
</script>

<style scoped>
.operatore {
  font-size: 1.3rem;
  font-weight: bold;
}

.evento {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #eee;
}
</style>
